import { create } from 'zustand';
import { authService } from '../services/auth';

interface User {
  store_id: string;
  display_name: string;
  role: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  checkAuth: () => void;
  setTokens: (idToken: string, accessToken: string, refreshToken: string) => void;
}

const parseUserFromToken = (token: string): User | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;
    
    const rawData = atob(paddedBase64);
    const bytes = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
      bytes[i] = rawData.charCodeAt(i);
    }
    const jsonPayload = new TextDecoder().decode(bytes);
    const payload = JSON.parse(jsonPayload);
    
    return {
      store_id: payload['store_id'] || '',
      display_name: payload['display_name'] || '',
      role: payload['role'] || 'user'
    };
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isLoading: false,
  error: null,
  user: null,

  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });
    
    try {
      await authService.login({ username, password });
      const token = authService.getIdToken();
      const user = token ? parseUserFromToken(token) : null;
      set({ isAuthenticated: true, isLoading: false, user });
    } catch (error: any) {
      set({ 
        error: error.message || 'ログインに失敗しました',
        isLoading: false,
        isAuthenticated: false,
        user: null
      });
    }
  },

  logout: () => {
    authService.logout();
    set({ isAuthenticated: false, error: null, user: null });
  },

  clearError: () => {
    set({ error: null });
  },

  checkAuth: () => {
    const isAuth = authService.isAuthenticated();
    const token = authService.getIdToken();
    const user = token && isAuth ? parseUserFromToken(token) : null;
    set({ isAuthenticated: isAuth, user });
  },

  setTokens: (idToken: string, accessToken: string, refreshToken: string) => {
    localStorage.setItem('idToken', idToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    const user = parseUserFromToken(idToken);
    set({ isAuthenticated: true, user });
  },
}));
